import { getTrackedChangesState, TrackedChangesPluginState, TrackedChangesTrackedMetaKey } from '@common/prosemirror/plugins/tracked-changes.plugin';
import { getParentFromNodeSelection } from '@common/prosemirror/state/selection';
import { ChangeType } from '@common/prosemirror/tracked-changes/change-type.enum';
import { copySliceWithChanges } from '@common/prosemirror/tracked-changes/copy';
import { Node, NodeType, ResolvedPos } from 'prosemirror-model';
import { EditorState, NodeSelection, Selection, TextSelection, Transaction } from 'prosemirror-state';
import { CellSelection } from 'prosemirror-tables';

/**
 * Taken from prosemirror-tables and modified to apply a tracked removal instead of simply deleting the content.
 */
export function deleteCellSelection(tr: Transaction, selection: CellSelection, trackedChangesState: TrackedChangesPluginState) {
  const mapFrom = tr.steps.length;
  const ranges = selection.ranges;

  for (let i = 0; i < ranges.length; i++) {
    const { $from, $to } = ranges[i];
    const mapping = tr.mapping.slice(mapFrom);

    deleteRange(tr, tr.doc.resolve(mapping.map($from.pos)), tr.doc.resolve(mapping.map($to.pos)), trackedChangesState);
  }

  const sel = Selection.findFrom(tr.doc.resolve(tr.mapping.slice(mapFrom).map(selection.to)), -1);
  if (sel) {
    tr.setSelection(sel);
  }
}

/**
 * Taken from prosemirror-transform and modified to apply a tracked removal instead of simply deleting the content.
 */
export function deleteRange(tr: Transaction, $from: ResolvedPos, $to: ResolvedPos, trackedChangesState: TrackedChangesPluginState) {
  // TODO: this function is not putting the change in the correct place when there are isolated nodes being deleted. eg a selection that crosses from a p below a table into the table.
  // The table case is tough. If rows are being deleted because the selection crosses entire nodes then the tracked change is supposed to go on the table itself.
  // But if this was a different isolated node like MadCap:correctFeedback then we'd want to track like normal.
  // The problem only happens if the content of the isolated node is being deleted. Deleting the isolated node itself works fine.
  // When inserting the tracked delete it seems to be putting a new isolated node into the isolated node. Maybe the isolated nodes can't become one from an open slice because they are isolated. This appears to be true.
  // Its hitting the default case at the end of the function below on line ~71 `replace(from, to);`

  // Helper function to replace a range with a tracked change removal
  function replace(from: number, to: number) {
    const content = tr.doc.slice(from, to, true);
    tr.replace(from, to, copySliceWithChanges(trackedChangesState.changeMarkType, content, trackedChangesState.makeTrackedChangeNodeAttrs(ChangeType.Remove)));
  }

  let from = $from.pos;
  let to = $to.pos;

  const covered = coveredDepths($from, $to);

  for (let i = 0; i < covered.length; i++) {
    const depth = covered[i];
    const last = i === covered.length - 1;

    if ((last && depth == 0) || $from.node(depth).type.contentMatch.validEnd) {
      return replace($from.before(depth), $to.after(depth));
    }

    if (depth > 0 && (last || $from.node(depth - 1).canReplace($from.index(depth - 1), $to.indexAfter(depth - 1)))) {
      return replace($from.before(depth), $to.after(depth));
    }
  }

  for (let d = 1; d <= $from.depth && d <= $to.depth; d++) {
    if (from - $from.start(d) == $from.depth - d && to > $from.end(d) && $to.end(d) - to != $to.depth - d && $from.start(d - 1) == $to.start(d - 1) && $from.node(d - 1).canReplace($from.index(d - 1), $to.index(d - 1))) {
      return replace($from.before(d), to);
    }
  }

  // The default case uses the selection's from/to but expands it to includes nodes if possible
  ({ from, to } = expandRangeToCoverNode($from, $to));
  replace(from, to);
}

/**
 * Expands a range to include all the nodes at the end of the range if the range ends at the end of the node's content.
 * For example, the selection below will be expanded to include the `MadCap:dropDownBody` and `MadCap:dropDown`
 * ```html
 * <p>Some te|xt</p>
 * <MadCap:dropDown>
 *   <MadCap:dropDownHead>
 *     <MadCap:dropDownHotspot>Dropdown head</MadCap:dropDownHotspot>
 *   </MadCap:dropDownHead>
 *   <MadCap:dropDownBody>
 *     <p>Dropdown body|</p>
 *   </MadCap:dropDownBody>
 * </MadCap:dropDown>
 * ```
 * @param $from The start of the range.
 * @param $to The end of the range.
 * @returns The expanded range.
 */
function expandRangeToCoverNode($from: ResolvedPos, $to: ResolvedPos): { from: number, to: number } {
  let from = $from.pos;
  let to = $to.pos;

  while ($to.depth > 0) {
    // If `to` is at the end of its node and `from` is less than the start of `to`'s node then move `to` up one depth
    if (to === $to.end() && from < $to.start()) {
      to = $to.after();
      $to = $to.doc.resolve(to);
    } else {
      break;
    }
  }

  return { from, to };
}

/**
 * Taken from prosemirror-transform and left unmodified.
 * Returns an array of all depths for which $from - $to spans the
 * whole content of the nodes at that depth.
 */
function coveredDepths($from: ResolvedPos, $to: ResolvedPos): number[] {
  let result: number[] = [], minDepth = Math.min($from.depth, $to.depth)
  for (let d = minDepth; d >= 0; d--) {
    let start = $from.start(d)
    if (start < $from.pos - ($from.depth - d) ||
      $to.end(d) > $to.pos + ($to.depth - d) ||
      $from.node(d).type.spec.isolating ||
      $to.node(d).type.spec.isolating) break
    if (start == $to.start(d) ||
      (d == $from.depth && d == $to.depth && $from.parent.inlineContent && $to.parent.inlineContent &&
        d && $to.start(d - 1) == start - 1))
      result.push(d)
  }
  return result
}

/**
 * A command that will delete the selection if there is one.
 * If the content being deleted is a NodeSelection and is the last node of the parent then the parent will be filled with the default node type (if the parent spec defines fillIfContentDeleted).
 * @param state The current state of the editor.
 * @param dispatch If provided the the command will be dispatched.
 * @returns Return true if the command can run.
 */
export function deleteSelection(state: EditorState, dispatch: ProsemirrorDispatcher) {
  if (state.selection.empty) {
    return false;
  }

  if (dispatch) {
    const trackedChangesState = getTrackedChangesState(state);
    const tr = state.tr;

    let fill: { parent: Node, pos: number, nodeType?: NodeType };
    if (state.selection instanceof NodeSelection) {
      fill = getParentFromNodeSelection(state.selection);
      if (fill.parent.type.spec.fillIfContentDeleted && fill.parent.childCount === 1) {
        fill.nodeType = typeof fill.parent.type.spec.fillIfContentDeleted === 'string' ? state.schema.nodes[fill.parent.type.spec.fillIfContentDeleted] : fill.parent.type.contentMatch.defaultType;
      } else {
        fill = null;
      }
    }

    // If tracked changes are enabled
    if (trackedChangesState?.enabled) {
      if (state.selection instanceof CellSelection) {
        deleteCellSelection(tr, state.selection, trackedChangesState);
      } else {
        let $from = state.selection.$from;
        let $to = state.selection.$to;

        // If the parent should be filled then do it first because deleteRange adjusts itself based on the parent's content
        if (fill) {
          const fillNode = fill.nodeType.createAndFill(trackedChangesState.makeTrackedChangeNodeAttrs(ChangeType.Add));
          tr.replaceWith(fill.pos + 1, fill.pos + 1, fillNode);
          $from = tr.doc.resolve($from.pos + fillNode.nodeSize);
          $to = tr.doc.resolve($to.pos + fillNode.nodeSize);
        }

        deleteRange(tr, $from, $to, trackedChangesState);
      }

      if (state.selection instanceof NodeSelection) {
        // Set the selection to just before the deleted node
        tr.setSelection(TextSelection.near(tr.doc.resolve(state.selection.from - 1), -1));
      } else {
        // Otherwise set the selection to the beginning of the selection
        tr.setSelection(TextSelection.near(tr.doc.resolve(state.selection.from)));
      }
    }
    // Else just delete the selection
    else {
      if (fill) {
        tr.replaceSelectionWith(fill.nodeType.createAndFill());
      } else {
        tr.deleteSelection();
      }
    }

    // If the deleted node was filled then set the selection
    if (fill?.nodeType.isAtom) {
      // Select the new node
      tr.setSelection(NodeSelection.create(tr.doc, fill.pos + 1));
    } else if (fill) {
      // Place the cursor inside the new node
      tr.setSelection(TextSelection.near(tr.doc.resolve(fill.pos + 1)));
    }

    // Mark that the changes were tracked
    if (trackedChangesState?.enabled) {
      tr.setMeta(TrackedChangesTrackedMetaKey, true);
    }

    dispatch(tr.scrollIntoView());
  };

  return true;
}
